"use client";
import { useFocusable } from "../focusable/focusable.js";
import { createElement, createHook, forwardRef, useEvent, useMergeRefs, useMetadataProps, useSafeLayoutEffect } from "@ariakit/react-utils";
import { disabledFromProps, fireClickEvent, isButton, isFirefox, isSelfTarget, isTextField, queueBeforeEvent } from "@ariakit/utils";
import { useEffect, useRef, useState } from "react";
//#region src/command/command.tsx
const TagName = "button";
function isNativeClick(event) {
	if (!event.isTrusted) return false;
	// istanbul ignore next: can't test trusted events yet
	const element = event.currentTarget;
	if (event.key === "Enter") return isButton(element) || element.tagName === "SUMMARY" || element.tagName === "A";
	if (event.key === " ") return isButton(element) || element.tagName === "SUMMARY" || element.tagName === "INPUT" || element.tagName === "SELECT";
	return false;
}
const symbol = Symbol("command");
/**
* Returns props to create a `Command` component. If the element is not a native
* clickable element (like a button), this hook will return additional props to
* make sure it's accessible.
* @see https://ariakit.com/components/command
* @example
* ```jsx
* const props = useCommand({ render: <div /> });
* <Role {...props}>Accessible button</Role>
* ```
*/
const useCommand = createHook(function useCommand({ clickOnEnter = true, clickOnSpace = true, ...props }) {
	const ref = useRef(null);
	const [isNativeButton, setIsNativeButton] = useState(false);
	useEffect(() => {
		if (!ref.current) return;
		setIsNativeButton(isButton(ref.current));
	}, []);
	const [active, setActive] = useState(false);
	const activeRef = useRef(false);
	const disabled = disabledFromProps(props);
	const [isDuplicate, metadataProps] = useMetadataProps(props, symbol, true);
	useSafeLayoutEffect(() => {
		if (!disabled) return;
		activeRef.current = false;
		setActive(false);
	}, [disabled]);
	const onKeyDownProp = props.onKeyDown;
	const onKeyDown = useEvent((event) => {
		onKeyDownProp?.(event);
		const element = event.currentTarget;
		if (event.defaultPrevented) return;
		if (isDuplicate) return;
		if (disabled) return;
		if (!isSelfTarget(event)) return;
		if (isTextField(element)) return;
		if (element.isContentEditable) return;
		const isEnter = clickOnEnter && event.key === "Enter";
		const isSpace = clickOnSpace && event.key === " ";
		const shouldPreventEnter = event.key === "Enter" && !clickOnEnter;
		const shouldPreventSpace = event.key === " " && !clickOnSpace;
		if (shouldPreventEnter || shouldPreventSpace) {
			event.preventDefault();
			return;
		}
		if (isEnter || isSpace) {
			const nativeClick = isNativeClick(event);
			if (isEnter) {
				if (!nativeClick) {
					event.preventDefault();
					const { view, ...eventInit } = event;
					const click = () => fireClickEvent(element, eventInit);
					if (isFirefox()) queueBeforeEvent(element, "keyup", click);
					else queueMicrotask(click);
				}
			} else if (isSpace) {
				activeRef.current = true;
				if (!nativeClick) {
					event.preventDefault();
					setActive(true);
				}
			}
		}
	});
	const onKeyUpProp = props.onKeyUp;
	const onKeyUp = useEvent((event) => {
		onKeyUpProp?.(event);
		if (isDuplicate) return;
		const isSpace = clickOnSpace && event.key === " ";
		if (!activeRef.current || !isSpace) return;
		const nativeClick = isNativeClick(event);
		activeRef.current = false;
		if (!nativeClick) setActive(false);
		if (event.defaultPrevented) return;
		if (!isSelfTarget(event)) return;
		if (disabled) return;
		if (event.metaKey) return;
		if (nativeClick) return;
		event.preventDefault();
		const element = event.currentTarget;
		const { view, ...eventInit } = event;
		queueMicrotask(() => fireClickEvent(element, eventInit));
	});
	const onBlurProp = props.onBlur;
	const onBlur = useEvent((event) => {
		onBlurProp?.(event);
		if (!activeRef.current) return;
		activeRef.current = false;
		setActive(false);
	});
	props = {
		"data-active": active || void 0,
		type: isNativeButton ? "button" : void 0,
		...metadataProps,
		...props,
		ref: useMergeRefs(ref, props.ref),
		onKeyDown,
		onKeyUp,
		onBlur
	};
	props = useFocusable(props);
	return props;
});
/**
* Renders a clickable element, which is a `button` by default, and inherits
* features from the [`Focusable`](https://ariakit.com/reference/focusable)
* component.
*
* If the base element isn't a native clickable one, this component will provide
* extra attributes and event handlers to ensure accessibility. It can be
* activated with the keyboard using the
* [`clickOnEnter`](https://ariakit.com/reference/command#clickonenter) and
* [`clickOnSpace`](https://ariakit.com/reference/command#clickonspace)
* props. Both are set to `true` by default.
* @see https://ariakit.com/components/command
* @example
* ```jsx
* <Command>Button</Command>
* ```
*/
const Command = forwardRef(function Command(props) {
	return createElement(TagName, useCommand(props));
});
//#endregion
export { Command, useCommand };

//# sourceMappingURL=command.js.map